import React, { useState, useRef, useEffect, useMemo } from 'react';
import { cn } from '../../utils/cn';
import { ChevronDown, Search, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export interface SelectOption {
  label: string;
  value: string;
  icon?: string;
}

export interface SelectProps {
  label?: string;
  required?: boolean;
  options: SelectOption[];
  error?: string;
  className?: string;
  placeholder?: string;
  value?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchable?: boolean;
  disabled?: boolean;
  name?: string;
  position?: 'top' | 'bottom' | 'up' | 'down';
  triggerClassName?: string;
}

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      label,
      required = false,
      options = [],
      error,
      placeholder = 'Select option...',
      value,
      onChange,
      searchable = false,
      disabled = false,
      name,
      position = 'bottom',
      triggerClassName,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const hiddenSelectRef = useRef<HTMLSelectElement>(null);

    // Merge refs for the hidden select
    useEffect(() => {
      if (!ref) return;
      if (typeof ref === 'function') {
        ref(hiddenSelectRef.current);
      } else {
        (ref as React.MutableRefObject<HTMLSelectElement | null>).current = hiddenSelectRef.current;
      }
    }, [ref]);

    const selectedOption = useMemo(() =>
      options.find(opt => opt.value === value),
      [options, value]
    );

    const filteredOptions = useMemo(() => {
      if (!searchQuery) return options;
      return options.filter(opt =>
        opt.label.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }, [options, searchQuery]);

    const handleSelect = (optionValue: string) => {
      if (disabled) return;

      // Trigger native-like change event
      if (hiddenSelectRef.current) {
        hiddenSelectRef.current.value = optionValue;
      }

      if (onChange) {
        const event = {
          target: {
            name: name || '',
            value: optionValue,
          },
          persist: () => { },
        } as unknown as React.ChangeEvent<HTMLSelectElement>;
        onChange(event);
      }

      setIsOpen(false);
      setSearchQuery('');
    };

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <div className={cn("flex flex-col relative w-full", className)} ref={containerRef}>
        {/* Hidden native select for form accessibility/compatibility */}
        <select
          ref={hiddenSelectRef}
          value={value}
          onChange={onChange}
          className="hidden"
          name={name}
          disabled={disabled}
          {...props}
        >
          <option value="">{placeholder}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        {label && (
          <label className="block text-sm lg:text-base font-semibold text-text mb-2">
            {label}
            {required && <span className="text-[#F03434] ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {/* Custom Trigger */}
          <button
            type="button"
            disabled={disabled}
            onClick={() => !disabled && setIsOpen(!isOpen)}
            className={cn(
              'flex py-2 w-full items-center justify-between rounded-lg border border-muted bg-foreground px-4 text-base text-text ring-offset-background transition-all',
              'hover:border-teal/50 focus:outline-none focus:ring-2 focus:ring-teal/20 focus:border-teal',
              isOpen && 'border-teal ring-2 ring-teal/20',
              error && 'border-red-500',
              disabled && 'bg-gray-100 text-gray-500 cursor-not-allowed',
              'cursor-pointer',
              triggerClassName
            )}
          >
            <div className="flex items-center gap-3 truncate">
              {selectedOption?.icon && (
                <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-gray-50">
                  <img src={selectedOption.icon} alt="" className="h-full w-full object-contain" />
                </div>
              )}
              <span className={cn("truncate", !selectedOption && "text-text3")}>
                {selectedOption ? selectedOption.label : placeholder}
              </span>
            </div>
            <ChevronDown
              className={cn(
                'h-5 w-5 transition-transform duration-200 ml-2',
                isOpen && 'rotate-180',
                triggerClassName ? 'text-inherit' : 'text-gray-400'
              )}
            />
          </button>

          {/* Dropdown Menu */}
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: (position === 'top' || position === 'up') ? 10 : -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: (position === 'top' || position === 'up') ? 10 : -10 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className={cn(
                  "absolute z-50 w-full overflow-hidden rounded-lg border border-muted bg-white shadow-xl shadow-teal/5",
                  (position === 'top' || position === 'up') ? "bottom-full mb-2" : "mt-2"
                )}
              >
                {searchable && (
                  <div className="sticky top-0 border-b border-muted bg-white p-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                      <input
                        autoFocus
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-9 w-full rounded-lg bg-gray-50 pl-9 pr-3 text-sm text-text outline-none focus:ring-1 focus:ring-teal/50"
                        onKeyDown={(e) => {
                          if (e.key === 'Escape') setIsOpen(false);
                          e.stopPropagation();
                        }}
                      />
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 hover:bg-gray-200"
                        >
                          <X className="h-3 w-3 text-gray-500" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                <div className="max-h-60 overflow-y-auto p-1 scrollbar-thin scrollbar-thumb-teal/20 scrollbar-track-transparent">
                  {filteredOptions.length > 0 ? (
                    filteredOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option.value)}
                        className={cn(
                          "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors",
                          value === option.value
                            ? "bg-teal/10 text-teal font-semibold"
                            : "text-text hover:bg-teal/5"
                        )}
                      >
                        {option.icon && (
                          <div className="flex h-6 w-6 shrink-0 items-center justify-center overflow-hidden rounded-sm bg-gray-50">
                            <img src={option.icon} alt="" className="h-full w-full object-contain" />
                          </div>
                        )}
                        <span className="truncate">{option.label}</span>
                      </button>
                    ))
                  ) : (
                    <div className="px-3 py-4 text-center text-sm text-gray-500">
                      No results found
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
